/**
 * Topology system: generates a simple closed rectilinear cycle on the grid.
 * Loop closure and self-avoidance are invariants of the construction —
 * realization can never break them.
 *
 * Algorithm: seeded rectangle perimeter, then K mutations drawn from three
 * families, each preserving simplicity:
 * - bump: shift a straight run's interior sideways by one cell (1-cell
 *   clearance margin around the detour, checked against the whole path).
 * - U-extrude: grow a peninsula off a straight segment — out k, across,
 *   back k. The two columns may be mutually adjacent (m=2 hairpins host the
 *   `bend` U-turn pieces; wider ones host bend-medium/large windows).
 * - staircase: shift a run's interior two cells sideways via diagonal
 *   zig-zag transitions, whose LRLR signatures host the s-curve pieces.
 */
import type { Dir } from "../shared/types.ts";
import type { Rng } from "./rng.ts";
import {
  applyXform,
  DIR_STEP,
  moveLabel,
  opposite,
  turnLeft,
  turnRight,
  windowXform,
} from "./geom.ts";

export interface SkeletonCell {
  x: number;
  z: number;
  dirIn: Dir;
  dirOut: Dir;
  move: "F" | "L" | "R";
}

export interface Skeleton {
  cells: SkeletonCell[];
  /**
   * Ring-index pairs that share one grid cell — designated grade crossings
   * (the pierce mutation). Both visits travel straight and perpendicular to
   * each other at the same base height; realization puts the 4-way `cross`
   * piece there. Every other cell is visited exactly once.
   */
  crossings: { a: number; b: number }[];
  /**
   * Full-helix ensembles (the helix-loop mutation, at most one): the window
   * [start, start+len) traces the spiral block's perimeter and re-visits its
   * first cells as the ground-level underpass. Elevation MUST reserve the
   * named def here (drop 2.0) plus a 2.0 climb elsewhere — the duplicated
   * cells only compose as that one piece.
   */
  helixLoops: { start: number; len: number; id: string }[];
  /**
   * Pierce layout, when the ring was pierced (its splice lands at index 0:
   * anchor turn, column A cells at 1..k, column B at k+1..2k, the far
   * anchor at 2k+1; crossings' upper/column visits are indices d and
   * 2k+1−d). `bridge` = the hairpin passes OVER the crossed run at +1.0
   * (elevation reserves the climb/descent on the columns) instead of a
   * flat grade crossing; upper deck cells never take support pillars (the
   * lower channel owns the ground).
   */
  pierce?: { d: number; k: number; bridge: boolean };
}

type P = readonly [number, number];
const key = (x: number, z: number) => `${x},${z}`;

function dirBetween(a: P, b: P): Dir {
  const dx = b[0] - a[0];
  const dz = b[1] - a[1];
  if (dx === 1 && dz === 0) return 0;
  if (dx === 0 && dz === 1) return 1;
  if (dx === -1 && dz === 0) return 2;
  if (dx === 0 && dz === -1) return 3;
  throw new Error(`cells not adjacent: ${a} → ${b}`);
}

export function generateSkeleton(rng: Rng): Skeleton {
  // Seed rectangle perimeter, clockwise from origin.
  const w = rng.int(5, 10);
  const h = rng.int(4, 8);
  const ring: P[] = [];
  for (let x = 0; x < w - 1; x++) ring.push([x, 0]);
  for (let z = 0; z < h - 1; z++) ring.push([w - 1, z]);
  for (let x = w - 1; x > 0; x--) ring.push([x, h - 1]);
  for (let z = h - 1; z > 0; z--) ring.push([0, z]);
  // Half the tracks run the other way around. Without this every loop is
  // turn-left-dominant and the left-handed helix ensembles (which need
  // right-turn anchors with open ground) essentially never fit.
  if (rng.chance(0.5)) ring.reverse();

  const cells = new Set(ring.map(([x, z]) => key(x, z)));

  const mutations = rng.int(4, 16);
  let applied = 0;
  const mutRng = rng.child("mutations");
  for (
    let attempt = 0;
    attempt < mutations * 10 && applied < mutations;
    attempt++
  ) {
    const kind = mutRng.weighted(
      [["bump", 6], ["u-extrude", 2.5], ["staircase", 1.5]] as const,
    );
    const ok = kind === "bump"
      ? tryBump(ring, cells, mutRng)
      : kind === "u-extrude"
      ? tryUExtrude(ring, cells, mutRng)
      : tryStaircase(ring, cells, mutRng);
    if (ok) applied++;
  }

  // Duplicate-cell mutations run LAST (and mutually exclusive): the other
  // mutations' clearance logic assumes cell keys are unique on the path
  // (their `removed` bookkeeping would mis-handle a twice-visited cell).
  const pierceRng = rng.child("pierce");
  let pierce: { d: number; k: number; bridge: boolean } | null = null;
  if (pierceRng.chance(0.55)) {
    for (let attempt = 0; attempt < 120 && !pierce; attempt++) {
      pierce = tryPierce(ring, cells, pierceRng);
    }
  }
  const loopRng = rng.child("helix-loop");
  let loop: { len: number; id: string } | null = null;
  if (!pierce && loopRng.chance(0.45)) {
    loop = tryHelixLoop(ring, cells, loopRng);
  }

  // Assemble cells with in/out dirs.
  const n = ring.length;
  const out: SkeletonCell[] = ring.map((c, i) => {
    const prev = ring[(i - 1 + n) % n];
    const next = ring[(i + 1) % n];
    const dirIn = dirBetween(prev, c);
    const dirOut = dirBetween(c, next);
    return { x: c[0], z: c[1], dirIn, dirOut, move: moveLabel(dirIn, dirOut) };
  });

  // Normalize: rotate so index 0 is a turn (straight runs never wrap the
  // seam, which keeps run-finding in the elevation system simple). A helix
  // loop's splice puts its window at ring index 0, and its first cell is a
  // turn, so normalization is the identity there and the declared start
  // stays 0.
  const firstTurn = out.findIndex((c) => c.move !== "F");
  const norm = out.slice(firstTurn).concat(out.slice(0, firstTurn));

  // Crossings = cells appearing twice (pierce). A helix loop's duplicates
  // live inside its own declared window and are NOT crossings.
  const seen = new Map<string, number>();
  const crossings: { a: number; b: number }[] = [];
  const loopLen = loop?.len ?? 0;
  norm.forEach((c, i) => {
    if (i < loopLen) return;
    const k = key(c.x, c.z);
    const first = seen.get(k);
    if (first !== undefined) crossings.push({ a: first, b: i });
    else seen.set(k, i);
  });
  return {
    cells: norm,
    crossings,
    helixLoops: loop ? [{ start: 0, len: loop.len, id: loop.id }] : [],
    ...(pierce ? { pierce } : {}),
  };
}

/**
 * Try one bump mutation: pick a straight run s..e (inclusive, same travel
 * dir), shift its interior one cell to a side. Path p→s→…→e→q becomes
 * p→s→(s+n)→…→(e+n)→e→q. Succeeds only if the detour cells are free and
 * keep 1-cell clearance from the rest of the path.
 */
function tryBump(ring: P[], cells: Set<string>, rng: Rng): boolean {
  const n = ring.length;
  const i = rng.int(0, n);
  const runLen = rng.int(2, 5); // cells s..e inclusive
  // Collect run: consecutive cells all stepping the same dir.
  const idx = (j: number) => (i + j + n) % n;
  const dir = dirBetween(ring[idx(0)], ring[idx(1)]);
  for (let j = 1; j < runLen; j++) {
    if (dirBetween(ring[idx(j - 1)], ring[idx(j)]) !== dir) return false;
  }
  // p and q must continue the path (any dir), just fetch them.
  const s = ring[idx(0)];
  const e = ring[idx(runLen - 1)];
  const p = ring[idx(-1)];
  const q = ring[idx(runLen)];

  const normal = rng.chance(0.5) ? turnLeft(dir) : turnRight(dir);
  const [nx, nz] = DIR_STEP[normal];

  const detour: P[] = [];
  for (let j = 0; j < runLen; j++) {
    const c = ring[idx(j)];
    detour.push([c[0] + nx, c[1] + nz]);
  }

  // Interior cells (strictly between s and e) leave the path.
  const removed = new Set<string>();
  for (let j = 1; j < runLen - 1; j++) {
    removed.add(key(ring[idx(j)][0], ring[idx(j)][1]));
  }

  // Allowed path-neighbors of detour cells: run endpoints, their path
  // neighbors (diagonal contact at the new corners), and the detour itself.
  const allowed = new Set([
    key(s[0], s[1]),
    key(e[0], e[1]),
    key(p[0], p[1]),
    key(q[0], q[1]),
    ...detour.map(([x, z]) => key(x, z)),
  ]);

  for (const [x, z] of detour) {
    if (cells.has(key(x, z)) && !removed.has(key(x, z))) return false;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        if (dx === 0 && dz === 0) continue;
        const k = key(x + dx, z + dz);
        if (cells.has(k) && !removed.has(k) && !allowed.has(k)) return false;
      }
    }
  }

  // Apply: splice the ring. `rest` holds q..p inclusive (n − runLen cells);
  // the interior cells between s and e drop out of the path.
  const rest: P[] = [];
  for (let j = runLen; j < n; j++) rest.push(ring[idx(j)]);
  const newRing = [s, ...detour, e, ...rest];
  if (newRing.length !== n + 2) return false; // sanity

  ring.length = 0;
  ring.push(...newRing);
  cells.clear();
  for (const [x, z] of ring) cells.add(key(x, z));
  return true;
}

/**
 * Validity core shared by the splice mutations: every `fresh` cell must be
 * off-path, and its 8-neighborhood must contain no path cell outside
 * `allowed` (cells in `removed` are leaving the path and don't count).
 */
function replaceOk(
  cells: Set<string>,
  fresh: readonly P[],
  removed: ReadonlySet<string>,
  allowed: ReadonlySet<string>,
): boolean {
  for (const [x, z] of fresh) {
    if (cells.has(key(x, z)) && !removed.has(key(x, z))) return false;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        if (dx === 0 && dz === 0) continue;
        const k = key(x + dx, z + dz);
        if (cells.has(k) && !removed.has(k) && !allowed.has(k)) return false;
      }
    }
  }
  return true;
}

/** Replace ring cells idx(i)..idx(i+m−1) with `sub` (same endpoints). */
function applySplice(
  ring: P[],
  cells: Set<string>,
  i: number,
  m: number,
  sub: readonly P[],
) {
  const n = ring.length;
  const idx = (j: number) => (i + j + n) % n;
  const rest: P[] = [];
  for (let j = m; j < n; j++) rest.push(ring[idx(j)]);
  ring.length = 0;
  ring.push(...sub, ...rest);
  cells.clear();
  for (const [x, z] of ring) cells.add(key(x, z));
}

/**
 * U-extrude: grow a peninsula off a straight segment of m cells — up k,
 * across, back down. The two columns ARE allowed to touch each other
 * (channels in adjacent cells clear each other: walls reach ±0.35).
 * m=2 tops become an L,L / R,R hairpin (the `bend` piece's window);
 * m=4/k≥2 host bend-medium ("FLFFLF"), m=6/k=3 host bend-large.
 */
function tryUExtrude(ring: P[], cells: Set<string>, rng: Rng): boolean {
  const n = ring.length;
  const i = rng.int(0, n);
  const idx = (j: number) => (i + j + n) % n;
  const m = rng.weighted([[2, 5], [4, 2.5], [6, 1.5]] as const);
  // Deep columns (k=5) host the large s-curve windows on their shoulders.
  const k = m === 2
    ? rng.int(1, 4)
    : m === 4
    ? rng.weighted([[2, 4], [3, 3], [4, 1], [5, 1.5]] as const)
    : rng.weighted([[3, 3], [4, 1], [5, 1.5]] as const);
  if (m + 2 > n) return false;
  const dir = dirBetween(ring[idx(0)], ring[idx(1)]);
  for (let j = 1; j < m; j++) {
    if (dirBetween(ring[idx(j - 1)], ring[idx(j)]) !== dir) return false;
  }
  const normal = rng.chance(0.5) ? turnLeft(dir) : turnRight(dir);
  const s = ring[idx(0)];
  const e = ring[idx(m - 1)];
  const p = ring[idx(-1)];
  const q = ring[idx(m)];
  // The anchors become turn cells — reject reversals.
  if (dirBetween(p, s) === opposite(normal)) return false;
  if (dirBetween(e, q) === normal) return false;

  const [nx, nz] = DIR_STEP[normal];
  const at = (c: P, j: number): P => [c[0] + nx * j, c[1] + nz * j];
  const colA: P[] = [];
  const colB: P[] = [];
  for (let j = 1; j <= k; j++) {
    colA.push(at(s, j));
    colB.push(at(e, j));
  }
  const top: P[] = [];
  for (let t = 1; t <= m - 2; t++) top.push(at(ring[idx(t)], k));

  const removed = new Set<string>();
  for (let t = 1; t <= m - 2; t++) {
    removed.add(key(ring[idx(t)][0], ring[idx(t)][1]));
  }
  const fresh = [...colA, ...top, ...colB];
  const allowed = new Set([
    key(p[0], p[1]),
    key(s[0], s[1]),
    key(e[0], e[1]),
    key(q[0], q[1]),
    ...fresh.map(([x, z]) => key(x, z)),
  ]);
  if (!replaceOk(cells, fresh, removed, allowed)) return false;

  const sub: P[] = [s, ...colA, ...top, ...colB.slice().reverse(), e];
  applySplice(ring, cells, i, m, sub);
  return true;
}

/**
 * Pierce: a hairpin peninsula (m=2 U-extrude) pushed THROUGH a perpendicular
 * straight run — the two column channels cross the run, making two adjacent
 * twice-visited cells (ring order visits each once along the run and once
 * along the column). Two forms:
 * - flat (grade crossing): the 4-way `cross` piece, everything at base 0.
 * - bridge (d ≥ 3): the hairpin climbs +1.0 on its columns and passes OVER
 *   the run; the deck piece above may be straight-hole — the marble's
 *   chance to fall onto the channel below (user rule: holes require track
 *   below).
 * The splice lands at ring index 0 (the anchor is a turn, so seam
 * normalization is the identity): columns at indices 1..k and k+1..2k,
 * upper crossing visits at d and 2k+1−d.
 */
function tryPierce(
  ring: P[],
  cells: Set<string>,
  rng: Rng,
): { d: number; k: number; bridge: boolean } | null {
  const n = ring.length;
  const i = rng.int(0, n);
  const idx = (j: number) => (i + j + n) % n;
  const s = ring[idx(0)];
  const e = ring[idx(1)];
  let dir: Dir;
  try {
    dir = dirBetween(s, e);
  } catch {
    return null;
  }
  const p = ring[idx(-1)];
  const q = ring[idx(2)];
  const normal = rng.chance(0.5) ? turnLeft(dir) : turnRight(dir);
  // The anchors become turn cells — reject reversals. The NEAR anchor must
  // genuinely turn (approach not already along the normal): the splice puts
  // it at ring index 0, and seam normalization is only the identity when
  // index 0 is a turn — the bridge layout indices depend on that.
  if (dirBetween(p, s) === opposite(normal)) return null;
  if (dirBetween(p, s) === normal) return null;
  if (dirBetween(e, q) === normal) return null;

  const [nx, nz] = DIR_STEP[normal];
  const at = (c: P, j: number): P => [c[0] + nx * j, c[1] + nz * j];

  // Scan outward for the first row where BOTH columns hit path cells — the
  // candidate crossed run. Rows before it must be completely free.
  let d = -1;
  for (let j = 1; j <= 6; j++) {
    const hitA = cells.has(key(...at(s, j)));
    const hitB = cells.has(key(...at(e, j)));
    if (hitA !== hitB) return null; // grazing one column only — bail
    if (hitA) {
      d = j;
      break;
    }
  }
  if (d < 2) return null; // no run found in reach, or hugging the segment

  const cA = at(s, d);
  const cB = at(e, d);
  // Both crossing cells must be interior of one straight run perpendicular
  // to the columns: their ring neighbors continue along ±dir.
  const runOk = (c: P, otherKey: string): boolean => {
    const along1 = key(c[0] + DIR_STEP[dir][0], c[1] + DIR_STEP[dir][1]);
    const along2 = key(c[0] - DIR_STEP[dir][0], c[1] - DIR_STEP[dir][1]);
    // One side continues to the partner crossing cell, the other must be a
    // path cell of the same run; the cell ahead (row d+1 handled by fresh
    // checks) must NOT be path.
    return (along1 === otherKey || cells.has(along1)) &&
      (along2 === otherKey || cells.has(along2));
  };
  if (!runOk(cA, key(...cB)) || !runOk(cB, key(...cA))) return null;
  // The crossing cells must currently be STRAIGHT visits of that run: find
  // them in the ring and check their in/out steps are along ±dir... their
  // ring neighbors must be exactly the along-run cells (no turns there).
  const straightVisit = (c: P): boolean => {
    for (let j = 0; j < n; j++) {
      if (ring[j][0] === c[0] && ring[j][1] === c[1]) {
        const prev = ring[(j - 1 + n) % n];
        const next = ring[(j + 1) % n];
        const din = dirBetween(prev, c);
        const dout = dirBetween(c, next);
        return din === dout && (din === dir || din === opposite(dir));
      }
    }
    return false;
  };
  if (!straightVisit(cA) || !straightVisit(cB)) return null;

  // Column length: at least one row beyond the crossing, at most a few.
  const k = d + 1 + rng.int(0, 3);

  const colA: P[] = [];
  const colB: P[] = [];
  for (let j = 1; j <= k; j++) {
    colA.push(at(s, j));
    colB.push(at(e, j));
  }
  // Fresh = every column cell except the crossings themselves.
  const fresh = [...colA, ...colB].filter(([x, z]) =>
    !(x === cA[0] && z === cA[1]) && !(x === cB[0] && z === cB[1])
  );
  const [tx, tz] = DIR_STEP[dir];
  const allowed = new Set([
    key(p[0], p[1]),
    key(s[0], s[1]),
    key(e[0], e[1]),
    key(q[0], q[1]),
    key(cA[0], cA[1]),
    key(cB[0], cB[1]),
    // The crossed run continues past the crossings on both sides; the
    // column cells touch those continuations diagonally.
    key(cA[0] - tx, cA[1] - tz),
    key(cB[0] + tx, cB[1] + tz),
    ...[...colA, ...colB].map(([x, z]) => key(x, z)),
  ]);
  if (!replaceOk(cells, fresh, new Set(), allowed)) return null;

  const sub: P[] = [s, ...colA, ...colB.slice().reverse(), e];
  applySplice(ring, cells, i, 2, sub);
  // Bridge needs room on the columns for the 1.0 climb/descent below the
  // crossing row (2 cells each side → d ≥ 3); prefer it when possible.
  const bridge = d >= 3 && rng.chance(0.7);
  return { d, k, bridge };
}

/**
 * Full-helix ensemble windows, keyed by the entry turn's chirality. Moves
 * MUST equal the matching registry defs' moves (pinned by a test): approach
 * turn, ccw/cw perimeter of the spiral block, then back through the first
 * cells as the ground-level underpass.
 */
export const HELIX_LOOP_SPECS: {
  large: boolean;
  turn: "L" | "R";
  id: string;
  moves: string;
}[] = [
  { large: false, turn: "R", id: "helix-left", moves: "RFLFFLFFLFFLFFF" },
  { large: false, turn: "L", id: "helix-right", moves: "LFRFFRFFRFFRFFF" },
  {
    large: true,
    turn: "R",
    id: "helix-large-left",
    moves: "RFFLFFFFLFFFFLFFFFLFFFFF",
  },
  {
    large: true,
    turn: "L",
    id: "helix-large-right",
    moves: "LFFRFFFFRFFFFRFFFFRFFFFF",
  },
];

/** Walk a moves string from (0,0) heading +z; returns the cell sequence. */
function walkMoves(moves: string): P[] {
  const out: P[] = [];
  let cx = 0, cz = 0;
  let dir: Dir = 1;
  for (const m of moves) {
    dir = m === "F" ? dir : m === "L" ? turnLeft(dir) : turnRight(dir);
    out.push([cx, cz]);
    cx += DIR_STEP[dir][0];
    cz += DIR_STEP[dir][1];
  }
  return out;
}

/**
 * Helix loop: replace a turn cell K (plus the following `under` straight
 * cells) with a full-helix ensemble window — the spiral block's perimeter
 * walk that re-visits K's row as the ground-level underpass. Duplicated
 * cells are confined to the window; realization is forced to the one
 * full-helix def by the elevation planner. The splice lands the window at
 * ring index 0 (K is a turn, so seam normalization keeps it there).
 *
 * All valid (anchor, spec) sites are enumerated and the pick is weighted
 * toward large blocks and the ring's MINORITY turn chirality (the seed
 * rectangle is turn-left-dominant, so left-handed helixes would otherwise
 * almost never appear — their R anchors mostly come from cluttered bump
 * detours).
 */
function tryHelixLoop(
  ring: P[],
  cells: Set<string>,
  rng: Rng,
): { len: number; id: string } | null {
  const n = ring.length;

  interface Site {
    newRing: P[];
    spec: (typeof HELIX_LOOP_SPECS)[number];
  }
  const validate = (
    i: number,
    spec: (typeof HELIX_LOOP_SPECS)[number],
  ): Site | null => {
    const idx = (j: number) => (i + j + n) % n;
    const K = ring[idx(0)];
    const P0 = ring[idx(-1)];
    const dirIn = dirBetween(P0, K);
    const dirOut = dirBetween(K, ring[idx(1)]);
    if (moveLabel(dirIn, dirOut) !== spec.turn) return null;
    const under = spec.large ? 3 : 2;
    if (n <= under + 2) return null;
    // The `under` cells after K must continue straight along dirOut, and
    // the exit edge after the last one must too.
    for (let j = 1; j <= under; j++) {
      if (dirBetween(ring[idx(j)], ring[idx(j + 1)]) !== dirOut) return null;
    }
    const Q = ring[idx(under + 1)];

    // Map the ensemble walk into the world anchored at K.
    const xf = windowXform(K[0], K[1], dirIn, 0);
    const toWorld = ([cx, cz]: P): P => {
      const w = applyXform(xf, [cx, 0, cz]);
      return [Math.round(w[0]), Math.round(w[2])];
    };
    const walk = walkMoves(spec.moves).map(toWorld);
    // Footprint = the walk's bounding box (perimeter + spiral column).
    const xs = walk.map((c) => c[0]);
    const zs = walk.map((c) => c[1]);
    const footprint: P[] = [];
    for (let x = Math.min(...xs); x <= Math.max(...xs); x++) {
      for (let z = Math.min(...zs); z <= Math.max(...zs); z++) {
        footprint.push([x, z]);
      }
    }
    const replaced = new Set<string>();
    replaced.add(key(K[0], K[1]));
    for (let j = 1; j <= under; j++) {
      replaced.add(key(ring[idx(j)][0], ring[idx(j)][1]));
    }
    const fresh = footprint.filter(([x, z]) => !replaced.has(key(x, z)));
    const allowed = new Set([
      key(P0[0], P0[1]),
      key(Q[0], Q[1]),
      ...replaced,
      ...footprint.map(([x, z]) => key(x, z)),
    ]);
    if (!replaceOk(cells, fresh, new Set(), allowed)) return null;

    // Splice with the window at index 0.
    const rest: P[] = [];
    for (let j = under + 1; j < n; j++) rest.push(ring[idx(j)]);
    const newRing = [...walk, ...rest];

    // The 2.0 climb needs a straight run of ≥4 cells outside the window.
    let runLen = 0;
    for (let j = spec.moves.length; j < newRing.length; j++) {
      const prev = newRing[j - 1];
      const cur = newRing[j];
      const next = newRing[(j + 1) % newRing.length];
      const straight = dirBetween(prev, cur) === dirBetween(cur, next);
      runLen = straight ? runLen + 1 : 0;
      if (runLen >= 4) return { newRing, spec };
    }
    return null;
  };

  // Minority chirality on this ring, for the rarity weighting.
  let l = 0, r = 0;
  for (let j = 0; j < n; j++) {
    const m = moveLabel(
      dirBetween(ring[(j - 1 + n) % n], ring[j]),
      dirBetween(ring[j], ring[(j + 1) % n]),
    );
    if (m === "L") l++;
    else if (m === "R") r++;
  }
  const minority = l >= r ? "R" : "L";

  const sites: Site[] = [];
  for (let i = 0; i < n; i++) {
    for (const spec of HELIX_LOOP_SPECS) {
      const site = validate(i, spec);
      if (site) sites.push(site);
    }
  }
  if (sites.length === 0) return null;
  const pick = rng.weighted(
    sites.map((s) =>
      [
        s,
        (s.spec.large ? 3 : 1) * (s.spec.turn === minority ? 3 : 1),
      ] as const
    ),
  );

  ring.length = 0;
  ring.push(...pick.newRing);
  cells.clear();
  for (const [x, z] of ring) cells.add(key(x, z));
  return { len: pick.spec.moves.length, id: pick.spec.id };
}

/**
 * Staircase: shift a run's interior TWO cells sideways with diagonal
 * zig-zag transitions. The transitions read L,R,L,R (and mirrored on the
 * way back), which is exactly the s-curve pieces' window signature.
 */
function tryStaircase(ring: P[], cells: Set<string>, rng: Rng): boolean {
  const n = ring.length;
  const i = rng.int(0, n);
  const idx = (j: number) => (i + j + n) % n;
  const span = rng.int(5, 9);
  if (span + 2 > n) return false;
  const dir = dirBetween(ring[idx(0)], ring[idx(1)]);
  for (let j = 1; j < span; j++) {
    if (dirBetween(ring[idx(j - 1)], ring[idx(j)]) !== dir) return false;
  }
  const normal = rng.chance(0.5) ? turnLeft(dir) : turnRight(dir);
  const s = ring[idx(0)];
  const e = ring[idx(span - 1)];
  const prev = ring[idx(-1)];
  const next = ring[idx(span)];
  if (dirBetween(prev, s) === opposite(normal)) return false;
  if (dirBetween(e, next) === normal) return false;

  const [nx, nz] = DIR_STEP[normal];
  const at = (c: P, j: number): P => [c[0] + nx * j, c[1] + nz * j];
  const c = (t: number) => ring[idx(t)];

  const sub: P[] = [s, at(s, 1), at(c(1), 1), at(c(1), 2)];
  for (let t = 2; t <= span - 2; t++) sub.push(at(c(t), 2));
  sub.push(at(c(span - 2), 1), at(e, 1), e);

  const removed = new Set<string>();
  for (let t = 1; t <= span - 2; t++) {
    removed.add(key(c(t)[0], c(t)[1]));
  }
  const fresh = sub.slice(1, -1);
  const allowed = new Set([
    key(prev[0], prev[1]),
    key(s[0], s[1]),
    key(e[0], e[1]),
    key(next[0], next[1]),
    ...fresh.map(([x, z]) => key(x, z)),
  ]);
  if (!replaceOk(cells, fresh, removed, allowed)) return false;

  applySplice(ring, cells, i, span, sub);
  return true;
}
